import TextInput from 'components/Common/Form/Input/TextInput'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import useToggle from 'hooks/useToggle'
import type { ChangeEventHandler } from 'react'
import { useCallback, useContext, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import type { GoogleAutocompletePlaceItem } from 'api/search'
import AddressInputOptions from './AddressInputOptions'
import ModalContext from 'contexts/ModalContext'
import AddressInputModal from './AddressInputModal'
import HiddenInput from '../HiddenInput'
import styled from 'styled-components'

const RelativeContainer = styled.div`
  position: relative;
`

export type AddressInputChangeHandler = (place: GoogleAutocompletePlaceItem) => void
interface Props extends Omit<React.ComponentProps<typeof TextInput>, 'onChange' | 'value'> {
  onChange?: AddressInputChangeHandler
  label?: string
  originLatitude?: number
  originLongitude?: number
  /**
   * Radius around the latitude/longitude given to return search results for in KM
   * Defaults to 100km
   * @default 100
   */
  radius?: number
  /**
   * Custom filter function to filter autocomplete predictions
   */
  filterPredictions?: (predictions: Array<GoogleAutocompletePlaceItem>) => Array<GoogleAutocompletePlaceItem>
  defaultPlaceId?: string
  placeId?: string
  defaultValue?: string
}

/**
 * Generic autocomplete input for searching for addresses
 * Please note, this uses google data proxied through svc-search for addresses.
 */
function AddressInput(props: Props) {
  const {
    name,
    onChange,
    required,
    originLatitude,
    originLongitude,
    defaultPlaceId,
    placeId,
    label,
    placeholder,
    defaultValue,
    radius = 100,
    filterPredictions,
    ...textProps
  } = props

  const intl = useIntl()
  const triggerRef = useRef<HTMLInputElement>(null)
  const [open, , openDropdown, closeDropdown] = useToggle()

  const showModal = useContext(ModalContext)
  const isMobileScreen = useScreenSizeOnly('mobile')

  const [localPlaceId, setLocalPlaceId] = useState<string | undefined>(defaultPlaceId)
  const [searchPhrase, setSearchPhrase] = useState<string | undefined>(defaultValue)
  const finalPlaceId = placeId ?? localPlaceId

  const searchPhraseInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    if (event.currentTarget.value) {
      openDropdown()
    }
    setSearchPhrase(event.currentTarget.value)
  }, [openDropdown])

  const onItemSelect = useCallback((option: GoogleAutocompletePlaceItem) => {
    setLocalPlaceId(option.placeId)
    onChange?.(option)
    const nextValue = intl.formatMessage(
      {
        id: 'rIo7GzgYFApp4WGaSjuu',
        defaultMessage: '{mainText}, {secondaryText}',
        description: 'Combined address display showing primary text and secondary text',
      },
      { mainText: option.mainText, secondaryText: option.secondaryText },
    )
    if (triggerRef.current) {
      triggerRef.current.value = nextValue
    }
    setSearchPhrase(nextValue)
    closeDropdown()
  }, [closeDropdown, intl, onChange])

  const onTextFocus = useCallback(async() => {
    if (isMobileScreen) {
      const result = await showModal<GoogleAutocompletePlaceItem | undefined>(<AddressInputModal
        label={label!}
        defaultPlaceId={placeId}
        originLatitude={originLatitude}
        originLongitude={originLongitude}
        placeholder={placeholder}
        defaultValue={searchPhrase}
        radius={radius}
        filterPredictions={filterPredictions}
      />)

      if (result) {
        onItemSelect?.(result)
      }
    } else if (searchPhrase) {
      openDropdown()
    }
  }, [isMobileScreen, label, onItemSelect, openDropdown, originLatitude, originLongitude, placeId, placeholder, radius, searchPhrase, showModal, filterPredictions])

  return <>
    <RelativeContainer>
      <HiddenInput
        name={name ? `${name}.addressPlaceId` : undefined}
        value={finalPlaceId}
      />
      <TextInput
        {...textProps}
        name={name ? `${name}.address` : undefined}
        placeholder={placeholder}
        onFocus={onTextFocus}
        required={required}
        label={label}
        autoComplete="off"
        onChange={searchPhraseInputChange}
        defaultValue={searchPhrase}
        ref={triggerRef}
        value={searchPhrase}
      />
    </RelativeContainer>
    <DropdownList
      size="fill-anchor"
      anchorRef={triggerRef}
      triggerRef={triggerRef}
      open={!isMobileScreen && open}
      onClose={closeDropdown}
    >
      <AddressInputOptions
        onSelect={onItemSelect}
        searchPhrase={searchPhrase}
        selectedPlaceId={placeId}
        originLatitude={originLatitude}
        originLongitude={originLongitude}
        radius={radius}
        filterPredictions={filterPredictions}
      />
    </DropdownList>
  </>
}

export default AddressInput
